import React, { useState } from 'react';
import { Container, Row, Col } from "react-bootstrap";

import TextInput from '../Parts/TextInput';
import StateSelector from '../Parts/StateSelector';
import Styles from '../Styles';
import Button from '../Parts/Button';
import type { EditableAddress, ExistingAddress, RootWithAlert } from './addressTypes';

const normalizeInitialAddress = (initialAddress?: ExistingAddress | null): EditableAddress => ({
	id: initialAddress?.id,
	address_id: initialAddress?.address_id,
	snapshot_id: initialAddress?.snapshot_id,
	creation_date: initialAddress?.creation_date,
	company_name: initialAddress?.company_name ?? '',
	first_name: initialAddress?.first_name ?? '',
	last_name: initialAddress?.last_name ?? '',
	address: initialAddress?.address ?? initialAddress?.address1 ?? '',
	address1: initialAddress?.address1 ?? null,
	address2: initialAddress?.address2 ?? '',
	state: initialAddress?.state ?? '',
	state_code: initialAddress?.state_code ?? '',
	country: initialAddress?.country ?? '',
	country_subdivision_id: initialAddress?.country_subdivision_id,
	city: initialAddress?.city ?? '',
	zip: initialAddress?.zip ?? '',
	phone: initialAddress?.phone ?? '',
	email: initialAddress?.email ?? '',
	is_residential: initialAddress?.is_residential === true
		|| (initialAddress?.is_residential as unknown) === 1,
	lift_gate: initialAddress?.lift_gate == 1,
});

const toApiAddress = (address: EditableAddress): ExistingAddress => {
	const {
		id,
		address_id: addressId,
		snapshot_id: snapshotId,
		creation_date: creationDate,
		country_subdivision_id: countrySubdivisionId,
		is_residential: isResidential,
		lift_gate: liftGate,
		...restAddress
	} = address;

	const apiAddress: ExistingAddress = {
		...restAddress,
		...(isResidential !== null ? { is_residential: isResidential } : {}),
		...(liftGate !== null ? { lift_gate: (liftGate ? 1 : 0) } : {}),
	};

	if (id !== undefined) {
		apiAddress.id = id;
	}
	if (addressId !== undefined) {
		apiAddress.address_id = addressId;
	}
	if (snapshotId !== undefined) {
		apiAddress.snapshot_id = snapshotId;
	}
	if (creationDate !== undefined) {
		apiAddress.creation_date = creationDate;
	}
	if (countrySubdivisionId !== undefined) {
		apiAddress.country_subdivision_id = countrySubdivisionId;
	}

	return apiAddress;
};

const Hr = () => (

	<hr
		style={{
			background: 'gray', border: 'none', height: '1px', margin: '1.4em 1em 1em 1em'
		}}
	/>
);

type PropertyAddressEditorProps = {
	onCancel: () => void;
	onSet: (address: ExistingAddress) => void;
	label: string;
	initialAddress: ExistingAddress | null | undefined;
	quoteType: string;
	ROOT: RootWithAlert;
};

const PropertyAddressEditor = ({
	onCancel,
	onSet,
	label,
	initialAddress,
	quoteType,
	ROOT
}: PropertyAddressEditorProps) => {
	const [address, setAddress] = useState<EditableAddress>(normalizeInitialAddress(initialAddress));

	const addressChanged = (options: Partial<EditableAddress>) => {
		const newAddress = { ...address, ...options };

		setAddress(newAddress);
	};

	const submitAddress = () => {
		const REQUIRED: Array<{ label: string; varName: keyof EditableAddress }> = [
			{ label: "Property Name", varName: "company_name" },
			{ label: "Address", varName: "address" },
			{ label: "State", varName: "country_subdivision_id" },
			{ label: "City", varName: "city" },
			{ label: "Zip / Postal Code", varName: "zip" },
		];

		const missingFields = REQUIRED.filter((req) =>
			address[req.varName] === null
				|| address[req.varName] === ""
				|| address[req.varName] === undefined);

		if (missingFields.length > 0) {
			ROOT.alert({
				icon: "error",
				title: "Missing Fields",
				text: (
					<span>
						Please fill out all required fields before setting this
						address.
						<br />
						<br />
						Missing Fields:
						<br />
						<ul>
							{missingFields.map((field) => (
								<li key={field.varName}>{field.label}</li>
							))}
						</ul>
					</span>
				),
				acceptButtonText: "OK",
				noCancelButton: true,
				quoteType
			})?.then(() => undefined);
		} else {
			onSet && onSet(toApiAddress(address));
		}
	};

	return (
		<div id="address-modal-wrapper">
			<div
				className="quote-address address-modal address-modal-two"
				style={{
					width: "30em",
					maxWidth: "95vw",
				}}
			>
				<Container className="local-bootstrap">
					<Row>
						<Col>
							<h4 className={`text-${quoteType}`}>{label || "New Address"}</h4>
						</Col>
					</Row>
					<Row>
						<Col>
							<TextInput
								style={{ width: "100%" }}
								label="Property Name"
								labelStyle={{ color: Styles.psGray }}
								onChange={(companyName) =>
									addressChanged({ company_name: companyName })}
								defaultValue={address.company_name}
								required
							/>
						</Col>
					</Row>

					<Hr />
					<Row>
						<Col>
							<TextInput
								label="Address Line 1"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ width: "100%", marginBottom: "0.5em" }}
								style={{ width: "100%" }}
								onChange={(newAddress) => addressChanged({ address: newAddress })}
								defaultValue={address.address}
								required
							/>
						</Col>
					</Row>
					<Row>
						<Col>
							<TextInput
								label="Address Line 2"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ width: "100%", marginBottom: "0.5em" }}
								style={{ width: "100%" }}
								onChange={(address2) => addressChanged({ address2 })}
								defaultValue={address.address2}
							/>
						</Col>
					</Row>

					<Row>
						<Col>
							<StateSelector
								key={address.country_subdivision_id}
								label="State / Province"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ flexGrow: "1", marginBottom: "0.5em" }}
								style={{ width: "100%" }}
								onChange={(countrySubdivisionId) =>
									addressChanged({ country_subdivision_id: countrySubdivisionId })}
								defaultValue={address.country_subdivision_id}
								required
							/>
						</Col>
					</Row>

					<Row>
						<Col>
							<TextInput
								label="City"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ flexGrow: "1", marginRight: "3%" }}
								style={{ width: "100%" }}
								onChange={(city) => addressChanged({ city })}
								defaultValue={address.city}
								required
							/>
						</Col>
						<Col>
							<TextInput
								label="Zip / Postal Code"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ flexGrow: "1" }}
								style={{ width: "100%" }}
								onChange={(zip) => addressChanged({ zip })}
								defaultValue={address.zip}
								required
							/>
						</Col>
					</Row>

					<Hr />
					<Row>
						<Col>
							<h3
								className={`text-${quoteType}`}
								style={{
									textAlign: "center",
									margin: "0",
									fontSize: "18px",
									fontWeight: "normal",
								}}
							>
								Optional Customer Info
							</h3>
						</Col>
					</Row>
					<Row>
						<Col>
							<div
								style={{
									color: Styles.psGray,
									margin: "10px 0",
									marginBottom: "15px",
									textAlign: "center",
									fontSize: "13px",
								}}
							>
								Customer data will NOT be used for marketing purposes.
							</div>
							{" "}
						</Col>
					</Row>
					<Row>
						<Col>
							<TextInput
								label="First Name"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ marginRight: "3%", flexGrow: "1" }}
								style={{ width: "100%" }}
								onChange={(firstName) =>
									addressChanged({ first_name: firstName })}
								defaultValue={address.first_name}
							/>
						</Col>
						<Col>
							<TextInput
								label="Last Name"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ flexGrow: "1" }}
								style={{ width: "100%" }}
								onChange={(lastName) => addressChanged({ last_name: lastName })}
								defaultValue={address.last_name}
							/>
						</Col>
					</Row>

					<Row>
						<Col>
							<TextInput
								label="Email"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ flexGrow: "1" }}
								style={{ width: "95%" }}
								onChange={(email) => addressChanged({ email })}
								defaultValue={address.email}
							/>
						</Col>
						<Col>
							<TextInput
								label="Phone"
								labelStyle={{ color: Styles.psGray }}
								wrapperStyle={{ flexGrow: "1" }}
								style={{ width: "100%" }}
								onChange={(phone) => addressChanged({ phone })}
								defaultValue={address.phone}
							/>
						</Col>
					</Row>
					<Row className="mt-2">
						<Col>
							<Button
								color="gray"
								onClick={onCancel}
							>
								Cancel
							</Button>
						</Col>
						<Col className="text-end">
							<Button
								color={quoteType ?? 'green'}
								onClick={submitAddress}
							>
								Set Address
							</Button>
						</Col>
					</Row>
				</Container>
			</div>
		</div>
	);
};

export default PropertyAddressEditor;
